import React, { useRef, useMemo, useEffect, useCallback, useState } from 'react';

interface SimpleViewportProps {
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
  outerScale?: number; // sheet canvas zoom
  children: React.ReactNode;
  onViewportChange?: (updates: { x: number; y: number; scale: number }) => void;
  isInteractive?: boolean;
  bounds?: { minX: number; minY: number; maxX: number; maxY: number };
  noTransform?: boolean; // when true, do not transform content (used for ReactFlow which manages its own viewport)
}

export const SimpleViewport = React.memo(function SimpleViewport(props: SimpleViewportProps) {
  const {
    x,
    y,
    scale,
    outerScale = 1,
    children,
    onViewportChange,
    isInteractive = false,
    bounds,
    noTransform = false,
  } = props;
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Constants for safe interaction bounds
  const CONTENT_SIZE = 100000; // Expand to handle larger schemas
  const MIN_SCALE = 0.01; // allow deep zoom-out
  const MAX_SCALE = 20;   // allow deeper zoom-in
  
  const clamp = (value: number, min: number, max: number) => {
    if (Number.isNaN(value) || !Number.isFinite(value)) return min;
    return Math.max(min, Math.min(max, value));
  };
  
  // Memoize styles to prevent re-renders
  const [isDragging, setIsDragging] = useState(false);
  const draggingRef = useRef(false);
  
  const viewportStyle = useMemo(() => ({
    position: 'absolute' as const,
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    overflow: 'hidden' as const,
    userSelect: 'none' as const,
    cursor: isInteractive ? (isDragging ? 'grabbing' : 'grab') : 'default',
  }), [isInteractive, isDragging]);
  
  const effectiveScale = useMemo(() => scale * (outerScale || 1), [scale, outerScale]);

  const contentStyle = useMemo(() => ({
    position: 'absolute' as const,
    top: 0,
    left: 0,
    // In noTransform mode, ReactFlow manages pan/zoom; we still scale by outerScale to respect sheet zoom
    transform: noTransform
      ? `scale(${outerScale})`
      : `translate(${-x * effectiveScale}px, ${-y * effectiveScale}px) scale(${effectiveScale})`,
    transformOrigin: '0 0',
    width: `${CONTENT_SIZE}px`,
    height: `${CONTENT_SIZE}px`,
    pointerEvents: 'auto' as const,
  }), [x, y, effectiveScale, noTransform, outerScale]);
  
  // Handle mouse wheel for zooming when interactive
  const handleWheel = useCallback((e: WheelEvent) => {
    console.log('SimpleViewport handleWheel:', { isInteractive, hasOnViewportChange: !!onViewportChange });
    // When ReactFlow manages its own viewport (noTransform), do not consume wheel
    if (noTransform) return;
    if (!isInteractive || !onViewportChange) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate zoom with more noticeable factor
    // Using exponential zoom for smoother experience
    const zoomSpeed = 0.0008;
    const scaleFactor = Math.exp(-e.deltaY * zoomSpeed);
    const unclampedScale = scale * scaleFactor;
    const newScale = clamp(unclampedScale, MIN_SCALE, MAX_SCALE);
    
    // Calculate mouse position relative to viewport
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    // viewport size derived from rect; props.width/height are controlled by parent wrapper
    
    // Calculate world coordinates under mouse before zoom
    const worldX = x + mouseX / (effectiveScale);
    const worldY = y + mouseY / (effectiveScale);
    
    // Calculate new viewport position to keep world point under mouse stationary
    // Note: keep internal scale separate; outerScale handled via effectiveScale
    let newX = worldX - mouseX / (newScale * (outerScale || 1));
    let newY = worldY - mouseY / (newScale * (outerScale || 1));

    // Wide symmetric bounds to allow free panning in any direction
    const minX = -CONTENT_SIZE;
    const maxX = CONTENT_SIZE;
    const minY = -CONTENT_SIZE;
    const maxY = CONTENT_SIZE;
    newX = clamp(newX, minX, maxX);
    newY = clamp(newY, minY, maxY);
    
    console.log('Zoom calculation:', {
      oldScale: scale,
      newScale,
      mousePos: { x: mouseX, y: mouseY },
      worldPos: { x: worldX, y: worldY },
      newViewportPos: { x: newX, y: newY }
    });
    
    // Optimistic local update via custom event (if parent listens)
    const detail = { x: newX, y: newY, scale: newScale };
    try {
      viewportRef.current?.dispatchEvent(new CustomEvent('viewport-local-update', { detail } as any));
    } catch {}
    onViewportChange({ x: newX, y: newY, scale: newScale });
  }, [isInteractive, onViewportChange, x, y, scale]);
  
  // Handle mouse drag for panning when interactive
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    console.log('SimpleViewport handleMouseDown:', { isInteractive, hasOnViewportChange: !!onViewportChange, button: e.button });
    // When ReactFlow manages its own viewport (noTransform), do not consume mouse down
    if (noTransform) return;
    if (!isInteractive || !onViewportChange) return;
    
    // LMB pan (0). Optionally allow MMB (1) too.
    if (e.button !== 0 && e.button !== 1) return;
    
    e.preventDefault();
    e.stopPropagation(); // Prevent parent handlers
    
    if (draggingRef.current) return;
    draggingRef.current = true;
    setIsDragging(true);
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startViewportX = x;
    const startViewportY = y;
    
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      
      let newX = startViewportX - deltaX / (effectiveScale);
      let newY = startViewportY - deltaY / (effectiveScale);

      // Wide symmetric bounds during pan as well
      const minX = -CONTENT_SIZE;
      const maxX = CONTENT_SIZE;
      const minY = -CONTENT_SIZE;
      const maxY = CONTENT_SIZE;
      newX = clamp(newX, minX, maxX);
      newY = clamp(newY, minY, maxY);
      
      console.log('Pan calculation:', {
        delta: { x: deltaX, y: deltaY },
        newViewportPos: { x: newX, y: newY }
      });
      
      const detail = { x: newX, y: newY, scale: clamp(scale, MIN_SCALE, MAX_SCALE) };
      try {
        viewportRef.current?.dispatchEvent(new CustomEvent('viewport-local-update', { detail } as any));
      } catch {}
      onViewportChange(detail);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      draggingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [isInteractive, onViewportChange, x, y, scale, setIsDragging]);
  
  // Register non-passive wheel event listener for proper preventDefault
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    
    const wheelHandler = (e: WheelEvent) => {
      handleWheel(e);
    };
    
    // Add non-passive listener
    viewport.addEventListener('wheel', wheelHandler, { passive: false });
    
    return () => {
      viewport.removeEventListener('wheel', wheelHandler);
    };
  }, [handleWheel]);
  
  return (
    <div
      ref={viewportRef}
      className="viewport-container"
      style={viewportStyle}
      onMouseDown={handleMouseDown}
      onMouseDownCapture={(e) => {
        // If ReactFlow handles viewport (noTransform), do not stop propagation
        if (noTransform) return;
        // Запускаем пан в capture, чтобы дети (ReactFlow) не перехватили событие
        if (isInteractive && (e.button === 0 || e.button === 1)) {
          e.preventDefault();
          handleMouseDown(e as unknown as React.MouseEvent);
          e.stopPropagation();
        }
      }}
      onDragStart={(e) => { e.preventDefault(); }}
      onContextMenu={(e) => { e.preventDefault(); }}
    >
      {/* Controls */}
      {isInteractive && onViewportChange && (
        <div style={{ position: 'absolute', right: 8, top: 8, zIndex: 10, display: 'flex', gap: 6 }}>
          <button
            onClick={(e) => {
              e.preventDefault(); e.stopPropagation();
              if (!bounds) return;
              const rect = viewportRef.current?.getBoundingClientRect();
              if (!rect) return;
              const viewportWidthPx = rect.width;
              const viewportHeightPx = rect.height;
              const padding = 100; // add world-units padding to avoid cutting node sizes
              const worldWidth = (bounds.maxX - bounds.minX) + padding * 2 || 1;
              const worldHeight = (bounds.maxY - bounds.minY) + padding * 2 || 1;
              const fitScale = Math.max(0.01, Math.min(MAX_SCALE, Math.min(viewportWidthPx / worldWidth, viewportHeightPx / worldHeight)));
              const startX = bounds.minX - padding;
              const startY = bounds.minY - padding;
              onViewportChange({ x: startX, y: startY, scale: fitScale });
            }}
            style={{ padding: '4px 6px', background: '#fff', border: '1px solid #ccc', borderRadius: 4 }}
            title="Показать всю схему"
          >
            ⤢
          </button>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onViewportChange({ x, y, scale: clamp(scale * 1.1, MIN_SCALE, MAX_SCALE) }); }}
            style={{ padding: '4px 8px', background: '#fff', border: '1px solid #ccc', borderRadius: 4 }}
            title="Увеличить"
          >
            +
          </button>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onViewportChange({ x, y, scale: clamp(scale / 1.1, MIN_SCALE, MAX_SCALE) }); }}
            style={{ padding: '4px 8px', background: '#fff', border: '1px solid #ccc', borderRadius: 4 }}
            title="Уменьшить"
          >
            −
          </button>
        </div>
      )}
      <div
        ref={contentRef}
        className="viewport-content"
        style={contentStyle}
      >
        {children}
      </div>
    </div>
  );
});
